import { useCreateGateway } from '../hooks/useCreateGateway'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Flex, Modal } from '@hivewatch/dark'

import {
  ControlledMultiSelectInput,
  ControlledTextInput,
} from '@/components/ui'
import { CreateEdgeDeviceInput } from '@/graphql/generated/schemas'

import { GatewayFormInputsIProps } from '../utils/utils'

interface DarkCreateGatewayModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link CreateGatewayModal} built on `@hivewatch/dark`.
 * Same mutation, validation and permission gating — only the chrome and the
 * two fields move to the design system.
 */
export const DarkCreateGatewayModal = ({
  isOpen,
  onClose,
}: DarkCreateGatewayModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableCreateGateway } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const { devicesOptions, create, isCreateLoading } = useCreateGateway()

  const { handleSubmit, control, errors } = useForm<GatewayFormInputsIProps>({
    reValidateMode: 'onChange',
  })

  const onSubmit: SubmitHandler<GatewayFormInputsIProps> = async (values) => {
    const name = values.name?.trim()
    const deviceIds = values.devices?.map((d) => d.value) || []
    const formData: CreateEdgeDeviceInput = { name, deviceIds }

    try {
      await create(formData)
      showSuccess(t('gateways:crud_create_success'))
      onClose()
    } catch (error) {
      showError(error)
    }
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!shouldEnableCreateGateway || isCreateLoading}
      isSaveLoading={isCreateLoading}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('common:save_changes')}
      showCancelButton
      size='compact'
      title={t('gateways:crud_create_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='CreateGatewayModal:name'
          disabled={isCreateLoading}
          errors={errors}
          label={t('gateways:crud_field_name_label')}
          name='name'
          placeholder={t('gateways:crud_field_name_placeholder')}
          rules={{ required: t('gateways:crud_field_name_required') }}
          width='100%'
        />

        <ControlledMultiSelectInput
          colorScheme={colorScheme}
          control={control}
          data-testid='CreateGatewayModal:devices'
          label={t('gateways:crud_field_devices_label')}
          maxDropdownHeight='200px'
          name='devices'
          options={devicesOptions}
          placeholder={t('gateways:crud_field_devices_placeholder')}
          resultsHeader={t('gateways:crud_field_devices_label')}
          searchable
          usePortal
        />
      </Flex>
    </Modal>
  )
}
